import {useEffect, useState} from 'react'
import {Link as RouterLink} from 'react-router-dom'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import Chip from '@mui/material/Chip'
import DeleteIcon from '@mui/icons-material/Delete'
import IconButton from '@mui/material/IconButton'
import GoodIcon from '@mui/icons-material/GppGood'
import KeyIcon from '@mui/icons-material/VpnKeyOutlined'
import RecoveryIcon from '@mui/icons-material/GroupsOutlined'
import RemoveModeratorIcon from '@mui/icons-material/RemoveModerator'
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/SettingsSuggest'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import WarningIcon from '@mui/icons-material/GppMaybe'

import Loader from '../shared/Loader'
import InfoTile from '../shared/InfoTile'

import {connect} from '../../state/hooks/tools'
import {isEmpty} from '../../lib/tools'
import {formatDate} from '../../lib/data'
import {readEndpoint, deleteResource} from '../../lib/asyncMethods'
import {REQUIRED_CONTACTS, isRecoveryOpen, readyContacts, recoveryNotice, recoverySummary} from '../../lib/recovery'
import {isTfaBypassed} from '../../lib/featureFlags'
import {TFA_CONFIGS, CLIENTS, PENDING_WRAPS, RECOVERY_INVITES, RECOVERY_REQUEST} from '../../state/actionTypes'

import './security.scss'

const Revoke = props => (
  <IconButton onClick={props.onClick} color='error' aria-label='delete' size='small' sx={{height: '34px', alignSelf: 'center'}}>
    <DeleteIcon />
  </IconButton>
)

/**
 * The headline recommendation, and how loudly to make it.
 *
 * An account with no way back in is the only state here that is actually bad, so it is the only one
 * carrying the error colour — the shield alone was doing too little to separate it from the tile
 * that says there is nothing to do. A recovery already under way is a warning rather than a fault,
 * and it outranks the advice to set one up: the contacts are named, and all that is left is to
 * finish.
 */
const securityRecommendation = ({recovery, recovering, recommendRecovery}) => {
  if (recovering)
    return {icon: WarningIcon, color: 'warning', label: 'Finish recovering your account', detail: recoveryNotice(recovery)}

  if (recommendRecovery) {
    return {
      icon: WarningIcon,
      color: 'error',
      label: 'Set up account recovery',
      detail: 'Without recovery contacts, losing your password means losing your account'
    }
  }

  return {icon: GoodIcon, label: 'Your account is protected', detail: 'No recommendation at this time'}
}

const Security = props => {
  const tfaConfig = props.tfaConfigs[0] || {}
  // an exempt account is never asked to enrol, so it has no config for the loader below to wait on
  const tfaBypassed = isTfaBypassed({settings: props.settings, userId: props.currentUser.id})
  const {recoveryInvites, pendingWraps} = props
  // 2-Step Verification gates this page's loader, so recovery can still be in flight when it
  // renders. Telling someone who has contacts that they have none is worse than telling them
  // nothing for a moment.
  const [recoveryLoaded, setRecoveryLoaded] = useState(false)
  const recoveryReady = readyContacts({invites: recoveryInvites, pendingWraps}).length >= REQUIRED_CONTACTS
  const recovering = isRecoveryOpen(props.recovery)
  const recommendRecovery = recoveryLoaded && !recoveryReady
  const recommendation = securityRecommendation({recovery: props.recovery, recovering, recommendRecovery})

  useEffect(() => {
    if (isEmpty(props.tfaConfigs) && !tfaBypassed)
      props.readEndpoint('tfa-configs')
  }, [tfaBypassed])

  useEffect(() => {
    if (isEmpty(props.clients))
      props.fetchClients('clients')
  }, [])

  useEffect(() => {
    Promise.all([
      props.fetchRecoveryInvites('recovery-invites'),
      props.fetchPendingWraps('recovery-key-shares/pending'),
      props.fetchRecovery('recovery-requests')
    ]).finally(() => setRecoveryLoaded(true))
  }, [])

  const revoke = clientId => () => {
    props.deleteResource(`sso/revoke/${clientId}`)
  }

  return (
    <Loader loading={isEmpty(props.tfaConfigs) && !tfaBypassed}>
      <Stack component='div' className='security'>
        <Typography variant='h4' gutterBottom>Security & sign-in</Typography>
        <InfoTile
          className='sub-heading'
          icon={recommendation.icon}
          iconColor={recommendation.color}
          label={recommendation.label}
          details={[recommendation.detail]}
        />
        <Box component='div' sx={{marginBottom: '15px'}}>
          <Typography variant='h6' gutterBottom>If you lose your password</Typography>
          <Typography sx={{marginBottom: '15px'}} variant='body2' gutterBottom>
            Recovery contacts each hold a piece of a key to your account. Any three of them together
            can let you back in
          </Typography>
          <InfoTile
            className='recovery-info'
            icon={RecoveryIcon}
            label='Account recovery'
            details={recoveryLoaded ? [recovering ? recoveryNotice(props.recovery) : recoverySummary({invites: recoveryInvites, pendingWraps})] : []}>
            <Button
              className='manage'
              component={RouterLink}
              to='/dashboard/recovery'
              size='small'>
              {recovering ? 'Continue' : (isEmpty(recoveryInvites) ? 'Set up' : 'Manage')}
            </Button>
          </InfoTile>
        </Box>
        <Box component='div' sx={{marginBottom: '15px'}}>
          <Typography variant='h6' gutterBottom>How you sign in</Typography>
          <Typography sx={{marginBottom: '15px'}} variant='body2' gutterBottom>
            Make sure you can always have access by keeping the information here up to date
          </Typography>
          <InfoTile className='tfa-info' icon={SecurityIcon} label='2-Step Verification' details={[]}>
            {
              tfaBypassed ? (
                <Chip
                  className='exempt'
                  sx={{bgcolor: 'rgba(255, 224, 178, 0.5)'}}
                  icon={<RemoveModeratorIcon color='warning' />}
                  label='Not required for this account'
                />
              ) : (
                <Chip
                  className='enabled'
                  sx={{bgcolor: 'rgba(190, 239, 187, 0.5)'}}
                  icon={<CheckCircleIcon color='success' />}
                  label={`Turned on since ${formatDate(tfaConfig.updatedAt)}`}
                />
              )
            }
          </InfoTile>
        </Box>
        <Box component='div' sx={{marginBottom: '15px'}}>
          <Typography variant='h6' gutterBottom>How this works</Typography>
          <InfoTile
            className='protection-info'
            icon={KeyIcon}
            iconColor='primary'
            label='How your account is protected'
            details={['What your key is, and when it can be opened']}>
            <Button
              className='manage'
              component={RouterLink}
              to='/dashboard/security-and-sign-in/protection'
              size='small'>
              Read
            </Button>
          </InfoTile>
        </Box>
        {
          (props.roles?.length > 0 && props.roles[0].name === 'user') && props.clients.length > 0 && (
            <Box component='div'>
              <Typography variant='h6' gutterBottom>Connected Apps</Typography>
              <Typography sx={{marginBottom: '15px'}} variant='body2' gutterBottom>
                The following apps have been granted access to your account. If something looks off, you can revoke access
              </Typography>
              {
                props.clients.map(
                  client => (
                    <InfoTile
                      key={client.id}
                      className='client'
                      icon={SettingsIcon}
                      label={client.name}
                      loading={props.loading}
                      details={[]}
                      action={Revoke}
                      revoke={revoke(client.id)}
                    />
                  )
                )
              }
            </Box>
          )
        }
      </Stack>
    </Loader>
  )
}

const mapStateToProps = ({tfa, clients, roles, recovery, pendingWraps, recoveryRequest, users, metadata}) => ({
  tfaConfigs: [],
  clients: [],
  ...roles,
  ...tfa,
  ...clients,
  currentUser: users.currentUser || {},
  settings: metadata.settings,
  recoveryInvites: recovery.recoveryInvites || [],
  pendingWraps: pendingWraps.pendingWraps || [],
  recovery: (recoveryRequest.recoveryRequest || [])[0] || {}
})

const actionCreators = ({tfa, clients, recovery, pendingWraps, recoveryRequest}) => ({
  readEndpoint: readEndpoint(tfa, TFA_CONFIGS),
  fetchClients: readEndpoint(clients, CLIENTS),
  deleteResource: deleteResource(clients, CLIENTS),
  fetchRecoveryInvites: readEndpoint(recovery, RECOVERY_INVITES),
  fetchPendingWraps: readEndpoint(pendingWraps, PENDING_WRAPS),
  fetchRecovery: readEndpoint(recoveryRequest, RECOVERY_REQUEST)
})

export default connect(mapStateToProps, actionCreators)(Security)
